import { Box, Button, Stack, SvgIcon } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateCourseRegistration as updateCourseRegistrationAction } from '../../../actions/course.actions';
import { fetchMindItem as fetchMindItemAction } from '../../../actions/mindItems.actions';
import { spaces } from '../../../assets/styles/themes/tokens';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { AddMenuButton } from '../../../components/cards/MuiCard/MuiCardBase/MuiCardBase';
import { routes } from '../../../constants';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import courseService from '../../../services/course.service';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import getMuiIcon, { type IconName } from '../../../utils/getMuiIcon';
import {
	getPageSourceFromQuery,
	getPageSourceFromState,
	resolveDashboardSection,
} from '../../../utils/pageSource';
import { trackContentViewed } from '../../../utils/tracking';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import CoursePlayerPageLoader from './CoursePlayerPageLoader';

const CoursePlayerPage = () => {
	const { t } = useTranslation();
	const { router, match } = useRouter();
	const [isFetching, setIsFetching] = useState(true);
	const [courseLaunchLink, setCourseLaunchLink] = useState<string | null>(null);
	const updateCourseRegistration = useAction(updateCourseRegistrationAction);
	const fetchCourseMindItem = useAction(fetchMindItemAction);
	const courseId = match.params.courseId;

	const mindItem = useSelector((state) => selectMindItem(state, courseId));

	// The cover page forwards the origin's page source here (SEAR-683); undefined
	// when the player was reached from the catalog / attachments / a direct link.
	const { query, state } = match.location;
	const pageSource = getPageSourceFromState(state) ?? getPageSourceFromQuery(query);
	const dashboardSection = resolveDashboardSection(pageSource, state, query);

	useEffect(() => {
		fetchCourseMindItem(courseId);
	}, [courseId, fetchCourseMindItem]);

	useEffect(() => {
		if (!mindItem || courseLaunchLink) return;

		courseService
			.buildCourseLaunchLink(courseId)
			.then(({ launchLink }) => {
				setCourseLaunchLink(launchLink);
				trackContentViewed(mindItem, { pageSource, dashboardSection }).catch(notifyBugsnagHandledError);
			})
			.finally(() => setIsFetching(false));
	}, [mindItem, courseLaunchLink, courseId, pageSource, dashboardSection]);

	useEffect(() => {
		const handleMessages = (event: MessageEvent) => {
			switch (event.data.eventType) {
				case 'courseRegistration':
					updateCourseRegistration(event.data.courseRegistration);
					break;
				case 'relaunchCourse':
					setIsFetching(true);
					setCourseLaunchLink(null);
					break;
			}
		};
		window.addEventListener('message', handleMessages);

		return () => {
			window.removeEventListener('message', handleMessages);
		};
	}, [updateCourseRegistration]);

	useEffect(() => {
		if (!isFetching && !courseLaunchLink) {
			router.replace(`/${routes.courseCover.root}/${courseId}`);
		}
	}, [isFetching, courseLaunchLink, router, courseId]);

	const navigateToAttachmentsPage = () => {
		router.push(`/${routes.courseAttachments.root.replace(':courseId', courseId)}`);
	};

	const customTopBarContent = (
		<Stack direction="row" alignItems="center" justifyContent="space-between">
			<Breadcrumbs
				links={[
					{
						title: t('Search'),
						url: `/${routes.courseCatalog.root}`,
						iconName: 'searchDone' as IconName,
					},
					{
						title: t('Course overview'),
						url: `/${routes.courseCatalog.root}/${courseId}`,
						iconName: 'book' as IconName,
					},
					{
						title: mindItem?.title,
						iconName: 'docText' as IconName,
					},
				]}
			/>
			<Stack direction="row" alignItems="center" gap={spaces.xs}>
				{mindItem && (
					<AddMenuButton
						isPinned={!!mindItem.isPinned}
						id={courseId}
						itemData={mindItem}
						type="course"
						size="medium"
						buttonSx={{
							position: 'relative',
							top: 'unset',
							right: 0,
						}}
					/>
				)}
				{mindItem && mindItem?.attachments.length > 0 && (
					<Button
						onClick={navigateToAttachmentsPage}
						startIcon={<SvgIcon component={getMuiIcon('paperclip')} />}
						variant="outlined"
						size="medium"
						color="primary"
						sx={{
							flexShrink: 0,
						}}
					>
						{mindItem.attachments.length} {t('Materials')}
					</Button>
				)}
			</Stack>
		</Stack>
	);

	return (
		<MasterLayout customTopBarContent={customTopBarContent} hideAvatar forceNewNavigation>
			{isFetching ? (
				<CoursePlayerPageLoader />
			) : (
				courseLaunchLink && (
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							'& > iframe': {
								width: '100% !important',
								height: '100% !important',
								border: '0 !important',
							},
						}}
					>
						<iframe src={courseLaunchLink} title={mindItem?.title} data-testid="course-player" />
					</Box>
				)
			)}
		</MasterLayout>
	);
};

export default CoursePlayerPage;
